import { useNavigation } from "@react-navigation/native";
import React, { useCallback, useMemo } from "react";
import { FlatList, Platform } from "react-native";
import { graphql, useLazyLoadQuery } from "react-relay";
import { WidgetTaskSingle } from "widgets";
import { LazyQueryNetworkBoundrary } from "../../../../../../components/mobile/QueryNetworkBoundrary";
import { getWidgetAddWidgetSizeWithinBounds } from "../../utils/useWidgetAddWidgetSizeState";
import {
  WACategoryNavigationProp,
  WACategoryScreenProps,
} from "../../WANavigation";
import { WACategoryWidgetContainer } from "../../WACategoryWidgetContainer";
import { taskSingleWidgetSizes } from "../../utils/widgetSizes/taskSingleWidgetSizes";
import { WACategoryKPIQuery } from "./__generated__/WACategoryKPIQuery.graphql";
import { WACategoryKPIEmpty } from "./WACategoryKPIEmpty";
import { useWACategoryLayout } from "../../utils/useWACategoryLayout";
import { useIntlTokens } from "../../../../../../components/mobile/useIntlTokens";
import { WACategoryFooterAdminButton } from "../../WACategoryAdminButtons";
import { AHSeparator } from "arkh";

type Props = WACategoryScreenProps<"WACategoryKPI">;

export function WACategoryKPI(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerWACategoryKPI {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

export function InnerWACategoryKPI(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<WACategoryKPIQuery>(
    graphql`
      query WACategoryKPIQuery($storecastId: ID!) {
        dashboard(id: $storecastId) @required(action: THROW) {
          previewWidgetTaskSingleKPIs {
            id
            title
            task {
              id
            }
            ...WidgetTaskSingle_widgetTaskSingle
          }
        }
      }
    `,
    {
      storecastId: props.route.params.storecastId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const onAdd = useCallback(
    (taskId: string) => {
      props.navigation.navigateDeprecated("WidgetAddKPI", {
        defaultTeams: props.route.params.defaultTeams,
        position: props.route.params.position,
        storecastId: props.route.params.storecastId,
        unitSize: props.route.params.unitSize,
        maxSize: props.route.params.maxSize,
        taskId,
      });
    },
    [
      props.navigation,
      props.route.params.defaultTeams,
      props.route.params.maxSize,
      props.route.params.position,
      props.route.params.storecastId,
      props.route.params.unitSize,
    ]
  );

  const [size, hasSizeWithinBounds] = useMemo(
    () =>
      getWidgetAddWidgetSizeWithinBounds({
        maxSize: props.route.params.maxSize,
        widgetSizes: taskSingleWidgetSizes,
      }),
    [props.route.params.maxSize]
  );

  const intl_tokens = useIntlTokens();

  const renderItem = useCallback(
    ({
      item: kpi,
    }: {
      item: (typeof data.dashboard.previewWidgetTaskSingleKPIs)[number];
    }) =>
      kpi?.task ? (
        <WACategoryWidgetContainer
          hasSizeWithinBounds={hasSizeWithinBounds}
          unitSize={props.route.params.unitSize}
          size={size}
          title={kpi.title}
          addTitle={intl_tokens.SelectMeasurementAreas}
          onAdd={() => {
            if (kpi?.task) {
              onAdd(kpi.task.id);
            }
          }}
        >
          <WidgetTaskSingle
            mode="IDLE"
            clientForceSize={
              size.w === 2 && size.h === 1 ? "TWO_X_ONE" : "ONE_X_ONE"
            }
            widget={kpi}
            fetchKey={props.fetchKey}
            storecastId={props.route.params.storecastId}
          />
        </WACategoryWidgetContainer>
      ) : null,
    [
      data,
      hasSizeWithinBounds,
      intl_tokens.SelectMeasurementAreas,
      onAdd,
      props.fetchKey,
      props.route.params.storecastId,
      props.route.params.unitSize,
      size,
    ]
  );

  const contentContainerStyle = useWACategoryLayout();

  if (data.dashboard.previewWidgetTaskSingleKPIs.length === 0) {
    return <WACategoryKPIEmpty />;
  }

  return (
    <FlatList
      style={{ flex: 1 }}
      ItemSeparatorComponent={() => <AHSeparator spaceBottom="large" />}
      contentContainerStyle={contentContainerStyle}
      keyExtractor={(item) => item.id}
      data={data.dashboard.previewWidgetTaskSingleKPIs}
      renderItem={renderItem}
      ListFooterComponent={ListFooterComponent}
    />
  );
}

function ListFooterComponent() {
  const navigation = useNavigation<WACategoryNavigationProp<"WACategoryKPI">>();
  const intl_tokens = useIntlTokens();
  return (
    <WACategoryFooterAdminButton
      title={intl_tokens.AdminKPI}
      onPress={() => {
        if (Platform.OS === "android") {
          navigation.navigateDeprecated("StoreAdmin", {
            screen: "KPIAdmin",
            params: {
              standalone: true,
            },
          });
          return;
        }
        const navigationType = navigation.getState().type;
        if (navigationType === "drawer") {
          navigation.pop();
        } else if (navigationType === "stack") {
          navigation.popToTop();
          navigation.goBack();
        }
        navigation.navigateDeprecated("StoreAdmin", {
          screen: "KPIAdmin",
          params: {
            standalone: true,
          },
        });
      }}
    />
  );
}
